"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const processSteps = [{
  title: "Initial Consultation & Record Review",
  description: "I begin with a thorough review of your medical records including PSA trends, MRI findings, and pathology reports. Together, I organize your clinical history to ensure every detail of your diagnosis is clearly documented and understood.",
  image: "https://myprostatepath.com/wp-content/uploads/2026/04/medical-record-scaled.jpg"
}, {
  title: "Diagnostic Interpretation & Education",
  description: "I translate complex medical terminology into clear language, explaining your staging, risk classification, and the implications of genomic or germline testing results like PHI or SelectMDx.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605152/img/shutterstock_2797903051-scaled.png"
}, {
  title: "Treatment Path Exploration",
  description: "I explore all available options—from active surveillance to surgery and radiation. I discuss how each path impacts your quality of life, focusing on urinary, sexual, and hormonal health.",
  image: "https://trusted-amoeba.10web.cloud/wp-content/uploads/2026/07/shutterstock_2115162608-scaled.jpg"
}, {
  title: "Decision Support & Clinical Readiness",
  description: "I help you prepare for specialist visits by developing meaningful questions for your care team, ensuring you and your family can move forward with confidence and clinical clarity.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605152/img/process_success_step.png"
}];
function TimelineStep({
  step,
  index
}: {
  step: (typeof processSteps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="90307" ref={ref} data-index={index} className={`relative flex flex-col md:flex-row items-center gap-8 md:gap-0 mb-24 last:mb-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${!isEven ? "md:flex-row-reverse" : ""}`}>
      {}
      <div className="absolute left-4 md:left-1/2 w-4 h-4 rounded-lg bg-secondary -translate-x-1/2 top-10 md:top-1/2 md:-translate-y-1/2 ring-8 ring-background z-10"></div>

      {}
      <div className={`w-full md:w-1/2 pl-12 md:pl-0 ${isEven ? "md:pr-16 md:text-right" : "md:pl-16 md:text-left"}`}>
        <div className={`font-serif font-bold text-6xl md:text-7xl text-secondary/40 mb-4 leading-none ${isEven ? "md:ml-auto" : ""}`}>
          {(index + 1).toString().padStart(2, "0")}
        </div>
        <h3 className="font-sans font-semibold text-2xl md:text-3xl text-foreground mb-4">
          {step.title}
        </h3>
        <p className="font-sans text-base md:text-lg text-foreground/70 leading-relaxed">
          {step.description}
        </p>
      </div>

      {}
      <div className={`w-full md:w-1/2 pl-12 md:pl-0 ${isEven ? "md:pl-16" : "md:pr-16"}`}>
        <div className="relative rounded-2xl overflow-hidden shadow-xl bg-muted">
          <img src={step.image} alt={step.title} className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700 ease-in-out" />
        </div>
      </div>
    </div>;
}
export default function TheWeightLossProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-support-process" className="bg-background py-24 md:py-32 px-6 md:px-8 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 md:mb-32 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif font-bold text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
            Your Navigation Journey
          </h2>
          <p className="font-sans text-lg md:text-xl text-foreground/70">
            A structured, empathetic approach designed to transform complex
            clinical data into actionable knowledge for you and your family.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-4 md:left-1/2 top-4 bottom-4 w-px border-l-2 border-dashed border-secondary/30 md:-translate-x-1/2"></div>

          {}
          <div className="relative z-10">
            {processSteps.map((step, index) => <TimelineStep key={index} step={step} index={index} />)}
          </div>
        </div>

        {}
        <div className="mt-24 text-center">
          <Button asChild size="lg" className="rounded-lg px-8 py-6 text-lg bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg transition-all">
            <Link to="/PatientBookingPage">Schedule Your Consultation</Link>
          </Button>
        </div>
      </div>
    </section>;
}